import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Entity, Query, Dimension, MatchTypeEnum } from "../../api/OasisBackendApi";
import { IconLoader2, IconInfoCircle, IconChevronDown, IconChevronUp, IconX } from "@tabler/icons-react";
import { computePCA, fitPCA, projectPointsThroughFit, PointWithData, PCAResult } from "../../utils/pca";
import { computeShimmerOrder, ShimmerPoint } from "../../utils/ShimmerIterator";
import { transformTo2D, transformFilteredTo2D, buildPointsWithData, Point2D as TransformerPoint2D, TransformationConfig } from "./CoordinateTransformer";

interface AnalysisVisualizationProps {
    entities: Entity[];
    queries: Query[];
    searchResults: (Entity | Query)[];
    searchResultEntities: Entity[];
    selectedDimensions: {
        x: Dimension | null;
        y: Dimension | null;
    };
    showCoverageHull: boolean;
    isLoading: boolean;
    onOverlapGroupSelect?: (group: Point2D[] | null, index: number) => void;
    uiMapping?: Record<string, string> | null;
    usePCAMode: boolean;
    pcaSelectedDimensions: Dimension[];
    onPcaResultChange?: (result: PCAResult | null) => void;
    isComparisonMode: boolean;
    selectedPoints: [any | null, any | null];
    onSelectedPointsChange: (points: [any | null, any | null]) => void;
    hoveredDimensionId: string | null;
    comparisonResult: any | null;
    queryMatches?: Record<string, MatchTypeEnum>;
    showVectorField?: boolean;
    vectorFieldMode?: "individual" | "average";
    vectorFieldLength?: "field" | "full";
    vectorFieldShimmer?: boolean;
    weightAdjustedMode?: boolean;
    queryWeights?: Record<string, number>;
    distanceMetric?: "cosine" | "euclidean" | "manhattan";
    useNormalizedEmbeddings?: boolean;
    pcaBasisSource?: "both" | "entities" | "queries";
}

// Point2D is now imported from CoordinateTransformer
type Point2D = TransformerPoint2D;

// Floating Status Info Panel Component
const StatusInfoPanel = ({
    usePCAMode,
    pcaResult,
    pcaSelectedDimensions,
    selectedDimensions,
    normalizedPoints,
    points
}: {
    usePCAMode: boolean;
    pcaResult: any;
    pcaSelectedDimensions: Dimension[];
    selectedDimensions: { x: Dimension | null; y: Dimension | null };
    normalizedPoints: Point2D[];
    points: Point2D[];
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isVisible, setIsVisible] = useState(true);

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-30">
            <div className="bg-gray-800/95 backdrop-blur-md border border-gray-700 rounded-lg shadow-xl">
                <div className="flex items-center gap-2 px-3 py-2">
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                    </button>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                        <IconInfoCircle size={14} />
                        <span>
                            {usePCAMode && pcaResult
                                ? `${normalizedPoints.length} points, ${pcaSelectedDimensions.length} dims`
                                : selectedDimensions.x && selectedDimensions.y 
                                ? `${normalizedPoints.length} points`
                                : 'Select dimensions'}
                        </span>
                    </div>
                    <button
                        onClick={() => setIsVisible(false)}
                        className="ml-2 text-gray-400 hover:text-white transition-colors"
                    >
                        <IconX size={14} />
                    </button>
                </div>
                
                {isExpanded && (
                    <div className="px-3 pb-3 pt-2 border-t border-gray-700 space-y-2">
                        {/* PCA Explained Variance */}
                        {usePCAMode && pcaResult && (
                            <div className="p-2 bg-blue-900/20 border border-blue-700/50 rounded text-xs">
                                <div className="text-blue-300 font-medium mb-1">Explained Variance</div>
                                <div className="text-blue-200 space-y-0.5">
                                    <div>PC1: {(pcaResult.explainedVariance.pc1 * 100).toFixed(1)}%</div>
                                    <div>PC2: {(pcaResult.explainedVariance.pc2 * 100).toFixed(1)}%</div>
                                    <div className="pt-1 border-t border-blue-700/50">Total: {(pcaResult.explainedVariance.total * 100).toFixed(1)}%</div>
                                </div>
                            </div>
                        )}
                        
                        {/* Value Range Info */}
                        {!usePCAMode && selectedDimensions.x && selectedDimensions.y && normalizedPoints.length > 0 && (
                            <div className="text-xs text-gray-400 space-y-0.5">
                                <div>X: {Math.min(...points.map(p => p.x)).toFixed(2)} to {Math.max(...points.map(p => p.x)).toFixed(2)}</div>
                                <div>Y: {Math.min(...points.map(p => p.y)).toFixed(2)} to {Math.max(...points.map(p => p.y)).toFixed(2)}</div>
                            </div>
                        )}
                        
                        {/* Full Status */}
                        <div className="text-xs text-gray-400">
                            {usePCAMode && pcaResult
                                ? `Showing ${normalizedPoints.length} points in PCA space (${pcaSelectedDimensions.length} dimensions)`
                                : selectedDimensions.x && selectedDimensions.y 
                                ? `Showing ${normalizedPoints.length} points in ${selectedDimensions.x.name} × ${selectedDimensions.y.name} space`
                                : 'Select dimensions to begin visualization'}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const AnalysisVisualization = ({
    entities,
    queries,
    searchResults,
    searchResultEntities,
    selectedDimensions,
    showCoverageHull,
    isLoading,
    onOverlapGroupSelect,
    uiMapping,
    usePCAMode,
    pcaSelectedDimensions,
    onPcaResultChange,
    isComparisonMode,
    selectedPoints,
    onSelectedPointsChange,
    hoveredDimensionId,
    comparisonResult,
    queryMatches = {},
    showVectorField = false,
    vectorFieldMode = "individual",
    vectorFieldLength = "field",
    vectorFieldShimmer = false,
    weightAdjustedMode = false,
    queryWeights = {},
    distanceMetric = "cosine",
    useNormalizedEmbeddings = false,
    pcaBasisSource = "both"
}: AnalysisVisualizationProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [hoveredPoint, setHoveredPoint] = useState<Point2D | null>(null);
    const [focusedVectorQueryId, setFocusedVectorQueryId] = useState<string | null>(null);
    const shimmerOrderRef = useRef<string[]>([]);
    const shimmerIndexRef = useRef(0);
    const shimmerTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [shimmerActiveQueryId, setShimmerActiveQueryId] = useState<string | null>(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
    const [pcaResult, setPcaResult] = useState<PCAResult | null>(null);
    const [pcaComputing, setPcaComputing] = useState(false);
    const onPcaResultChangeRef = useRef(onPcaResultChange);
    const [weightsExpanded, setWeightsExpanded] = useState(false);
    
    // Helper function to get color based on match type
    const getMatchTypeColor = useCallback((matchType: MatchTypeEnum | undefined, alpha: number = 0.7): { fill: string; stroke: string } => {
        const colors: Record<MatchTypeEnum, { fill: string; stroke: string }> = {
            'perfect_match': { fill: `rgba(16, 185, 129, ${alpha})`, stroke: `rgba(5, 150, 105, ${alpha + 0.1})` }, // emerald-500
            'good_match': { fill: `rgba(52, 211, 153, ${alpha})`, stroke: `rgba(16, 185, 129, ${alpha + 0.1})` }, // emerald-400
            'match': { fill: `rgba(251, 191, 36, ${alpha})`, stroke: `rgba(245, 158, 11, ${alpha + 0.1})` }, // amber-400
            'partial_match': { fill: `rgba(249, 115, 22, ${alpha})`, stroke: `rgba(234, 88, 12, ${alpha + 0.1})` }, // orange-500
            'irrelevant': { fill: `rgba(239, 68, 68, ${alpha})`, stroke: `rgba(220, 38, 38, ${alpha + 0.1})` }, // red-500
            'contradiction': { fill: `rgba(153, 27, 27, ${alpha})`, stroke: `rgba(127, 29, 29, ${alpha + 0.1})` } // red-800
        };
        
        if (!matchType) {
            // Default grey for entities without match info
            return { fill: `rgba(107, 114, 128, ${alpha})`, stroke: `rgba(75, 85, 99, ${alpha + 0.1})` }; // gray-500
        }
        
        return colors[matchType] || { fill: `rgba(107, 114, 128, ${alpha})`, stroke: `rgba(75, 85, 99, ${alpha + 0.1})` };
    }, []);
    
    // Update ref when callback changes
    useEffect(() => {
        onPcaResultChangeRef.current = onPcaResultChange;
    }, [onPcaResultChange]);

    // Helper function to extract image URLs from entity content
    const getImageUrls = useCallback((entity: Entity): string[] => {
        if (!uiMapping?.image_urls) return [];
        
        try {
            const imageData = entity.content[uiMapping.image_urls];
            if (typeof imageData === 'string') {
                // Try to parse as JSON first
                try {
                    const parsed = JSON.parse(imageData.replace(/'/g, '"'));
                    if (typeof parsed === 'string') {
                        // Parsed result is a single URL string
                        return [parsed];
                    } else if (Array.isArray(parsed)) {
                        // Parsed result is an array
                        return parsed;
                    } else {
                        return [];
                    }
                } catch {
                    // If JSON parsing fails, treat as single URL and convert to array
                    return [imageData];
                }
            } else if (Array.isArray(imageData)) {
                return imageData;
            } else {
                return [];
            }
        } catch (error) {
            console.error("Error parsing image urls", error);
            return [];
        }
    }, [uiMapping]);

    // Get transformation config based on current mode
    const getTransformationConfig = useCallback((): TransformationConfig => {
        if (usePCAMode && pcaResult) {
            return {
                mode: 'pca',
                pcaResult,
                pcaSelectedDimensions,
                useNormalized: useNormalizedEmbeddings
            };
        } else {
            return {
                mode: 'manual',
                selectedDimensions,
                useNormalized: useNormalizedEmbeddings
            };
        }
    }, [usePCAMode, pcaResult, pcaSelectedDimensions, selectedDimensions, useNormalizedEmbeddings]);

    // Apply weight transformation to points
    // Scaling depends on distance metric:
    // - Euclidean: sqrt(weight) because dist = sqrt(Σ w*(x-y)²)
    // - Manhattan: weight (linear) because dist = Σ w*|x-y|
    // - Cosine: sqrt(weight) because vectors are scaled by sqrt(w) before cosine
    const applyWeightTransformation = useCallback((points: Point2D[]): Point2D[] => {
        if (!weightAdjustedMode || Object.keys(queryWeights).length === 0 || usePCAMode) {
            return points;
        }
        
        const xDim = selectedDimensions.x?.name;
        const yDim = selectedDimensions.y?.name;
        
        if (!xDim || !yDim) return points;
        
        // Detect if weights are 0-1 or 0-100 scale
        const maxWeight = Math.max(...Object.values(queryWeights));
        const isNormalized = maxWeight <= 1;
        
        // Get weights and normalize to 0-1 range
        const rawWeightX = queryWeights[xDim] ?? (isNormalized ? 1 : 100);
        const rawWeightY = queryWeights[yDim] ?? (isNormalized ? 1 : 100);
        const weightX = isNormalized ? rawWeightX : rawWeightX / 100;
        const weightY = isNormalized ? rawWeightY : rawWeightY / 100;
        
        // Apply correct scaling based on distance metric
        let scaleX: number, scaleY: number;
        if (distanceMetric === "manhattan") {
            // Manhattan: linear scaling (w)
            scaleX = weightX;
            scaleY = weightY;
        } else {
            // Euclidean and Cosine: sqrt scaling
            scaleX = Math.sqrt(weightX);
            scaleY = Math.sqrt(weightY);
        }
        
        return points.map(point => ({
            ...point,
            x: point.x * scaleX,
            y: point.y * scaleY
        }));
    }, [weightAdjustedMode, queryWeights, selectedDimensions, usePCAMode, distanceMetric]);

    // Helper function to calculate coverage area from search result entities using transformer
    const getSearchResultCoverageArea = useCallback((): Point2D[] => {
        if (searchResultEntities.length === 0) {
            return [];
        }

        const config = getTransformationConfig();
        // Check if config is valid
        if (config.mode === 'manual' && (!config.selectedDimensions?.x || !config.selectedDimensions?.y)) {
            return [];
        }
        if (config.mode === 'pca' && !config.pcaResult) {
            return [];
        }

        const rawPoints = transformFilteredTo2D(searchResultEntities, entities, queries, searchResults, config);
        return applyWeightTransformation(rawPoints);
    }, [searchResultEntities, entities, queries, searchResults, getTransformationConfig, applyWeightTransformation]);

    // Convex hull algorithm (Graham scan)
    const getConvexHull = useCallback((points: Point2D[]): Point2D[] => {
        if (points.length < 3) return points;

        // Find the bottom-most point (and leftmost in case of tie)
        let bottomMost = points[0];
        for (let i = 1; i < points.length; i++) {
            if (points[i].y < bottomMost.y || (points[i].y === bottomMost.y && points[i].x < bottomMost.x)) {
                bottomMost = points[i];
            }
        }

        // Sort points by polar angle with respect to bottomMost
        const sortedPoints = points.slice().sort((a, b) => {
            const angleA = Math.atan2(a.y - bottomMost.y, a.x - bottomMost.x);
            const angleB = Math.atan2(b.y - bottomMost.y, b.x - bottomMost.x);
            if (angleA !== angleB) return angleA - angleB;
            // If angles are equal, sort by distance
            const distA = Math.pow(a.x - bottomMost.x, 2) + Math.pow(a.y - bottomMost.y, 2);
            const distB = Math.pow(b.x - bottomMost.x, 2) + Math.pow(b.y - bottomMost.y, 2);
            return distA - distB;
        });

        // Build convex hull
        const hull: Point2D[] = [];
        for (const point of sortedPoints) {
            while (hull.length >= 2) {
                const p1 = hull[hull.length - 2];
                const p2 = hull[hull.length - 1];
                const p3 = point;
                
                // Cross product to determine turn direction
                const cross = (p2.x - p1.x) * (p3.y - p1.y) - (p2.y - p1.y) * (p3.x - p1.x);
                if (cross <= 0) {
                    hull.pop();
                } else {
                    break;
                }
            }
            hull.push(point);
        }

        return hull;
    }, []);

    // Helper function to check if a point is inside a polygon
    const isPointInPolygon = useCallback((point: Point2D, polygon: Point2D[]): boolean => {
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            if (((polygon[i].y > point.y) !== (polygon[j].y > point.y)) &&
                (point.x < (polygon[j].x - polygon[i].x) * (point.y - polygon[i].y) / (polygon[j].y - polygon[i].y) + polygon[i].x)) {
                inside = !inside;
            }
        }
        return inside;
    }, []);

    // Helper function to calculate pixel-based density heatmap with ranking
    const calculateDensityHeatmap = useCallback((hullPoints: Point2D[], coveragePoints: Point2D[], canvasWidth: number, canvasHeight: number) => {
        if (hullPoints.length < 3 || coveragePoints.length === 0) return [];
        
        console.log('Calculating heatmap with:', {
            hullPoints: hullPoints.length,
            coveragePoints: coveragePoints.length,
            canvasSize: `${canvasWidth}x${canvasHeight}`
        });
        console.log('Coverage points:', coveragePoints.map(p => ({ 
            x: p.x.toFixed(1), 
            y: p.y.toFixed(1), 
            hasEntity: !!p.entity,
            entityId: p.entity?.id 
        })));
        console.log('Hull points:', hullPoints.map(p => ({ x: p.x.toFixed(1), y: p.y.toFixed(1) })));

        // Create a pixel-based density map for much finer detail
        const pixelDensity: number[][] = [];
        
        // Initialize pixel grid
        for (let y = 0; y < canvasHeight; y++) {
            pixelDensity[y] = [];
            for (let x = 0; x < canvasWidth; x++) {
                pixelDensity[y][x] = 0;
            }
        }
        
        // Calculate density for each pixel
        for (let y = 0; y < canvasHeight; y++) {
            for (let x = 0; x < canvasWidth; x++) {
                const pixelPoint = { x, y };
                
                // Only calculate density if the pixel is inside the hull AND close to at least one search result
                if (isPointInPolygon(pixelPoint, hullPoints)) {
                    let totalIntensity = 0;
                    const maxInfluenceRadius = 60; // Pixels - more reasonable radius to avoid hotspots in empty areas
                    const minDistanceToAnyResult = Math.min(...coveragePoints.map(p => 
                        Math.sqrt(Math.pow(p.x - x, 2) + Math.pow(p.y - y, 2))
                    ));
                    
                    // Only show heatmap if we're reasonably close to at least one search result
                    if (minDistanceToAnyResult > maxInfluenceRadius) {
                        continue; // Skip this pixel - too far from any search result
                    }
                    
                    // Calculate influence from each search result point
                    for (let i = 0; i < coveragePoints.length; i++) {
                        const point = coveragePoints[i];
                        const distance = Math.sqrt(
                            Math.pow(point.x - x, 2) + Math.pow(point.y - y, 2)
                        );
                        
                        if (distance <= maxInfluenceRadius) {
                            // Ranking weight: higher ranked results (lower index) contribute more
                            // First result gets weight 1.0, second gets 0.9, third gets 0.8, etc.
                            const rankingWeight = Math.max(0.1, 1.0 - (i * 0.1));
                            
                            // Distance weight: closer points contribute more (Gaussian-like)
                            const distanceWeight = Math.exp(-(distance * distance) / (2 * (maxInfluenceRadius / 4) * (maxInfluenceRadius / 4)));
                            
                            // Combined intensity
                            const intensity = rankingWeight * distanceWeight;
                            totalIntensity += intensity;
                        }
                    }
                    
                    pixelDensity[y][x] = totalIntensity;
                }
            }
        }
        
        return pixelDensity;
    }, [isPointInPolygon]);

    // Update canvas size on window resize
    useEffect(() => {
        const updateCanvasSize = () => {
            if (canvasRef.current) {
                const container = canvasRef.current.parentElement;
                if (container) {
                    // Get available space, accounting for padding and margins
                    const containerRect = container.getBoundingClientRect();
                    const availableWidth = Math.max(400, containerRect.width - 32); // Account for padding
                    const availableHeight = Math.max(300, Math.min(containerRect.height - 32, window.innerHeight - 300)); // Account for navbar and other UI
                    
                    setCanvasSize({ 
                        width: availableWidth, 
                        height: availableHeight 
                    });
                }
            }
        };

        updateCanvasSize();
        window.addEventListener('resize', updateCanvasSize);
        return () => window.removeEventListener('resize', updateCanvasSize);
    }, []);

    // Compute PCA when enabled (re-triggers when weights change in weight-adjusted mode)
    useEffect(() => {
        if (!usePCAMode || pcaSelectedDimensions.length < 2) {
            if (pcaResult !== null) {
                setPcaResult(null);
                onPcaResultChangeRef.current?.(null);
            }
            return;
        }

        setPcaComputing(true);
        
        const allPointsWithData = buildPointsWithData(entities, queries, searchResults);
        const weightsToApply = weightAdjustedMode && Object.keys(queryWeights).length > 0 
            ? queryWeights 
            : undefined;
        
        try {
            let result: PCAResult | null;

            if (pcaBasisSource === "both") {
                result = computePCA(entities, queries, pcaSelectedDimensions, allPointsWithData, weightsToApply, distanceMetric, useNormalizedEmbeddings);
            } else {
                const fittingPoints = allPointsWithData.filter(p =>
                    pcaBasisSource === "entities" ? !!p.entity : !!p.query
                );

                const fit = fitPCA(entities, queries, pcaSelectedDimensions, fittingPoints, weightsToApply, distanceMetric, useNormalizedEmbeddings);
                if (!fit) {
                    setPcaResult(null);
                    onPcaResultChangeRef.current?.(null);
                    setPcaComputing(false);
                    return;
                }

                const transformedPoints = projectPointsThroughFit(entities, queries, pcaSelectedDimensions, allPointsWithData, fit, weightsToApply, distanceMetric, useNormalizedEmbeddings);
                result = { ...fit, transformedPoints };
            }

            setPcaResult(result);
            onPcaResultChangeRef.current?.(result);
        } catch (error) {
            console.error('Error computing PCA:', error);
            setPcaResult(null);
            onPcaResultChangeRef.current?.(null);
        } finally {
            setPcaComputing(false);
        }
    }, [usePCAMode, pcaSelectedDimensions.length, entities.length, queries.length, searchResults.length, weightAdjustedMode, queryWeights, distanceMetric, useNormalizedEmbeddings, pcaBasisSource]);

    // Extract 2D coordinates from entities and queries using transformer
    const getPoints2D = useCallback((): Point2D[] => {
        const config = getTransformationConfig();
        const rawPoints = transformTo2D(entities, queries, searchResults, config);
        return applyWeightTransformation(rawPoints);
    }, [entities, queries, searchResults, getTransformationConfig, applyWeightTransformation]);

    // Normalize coordinates to canvas space and group overlapping points
    const normalizePoints = useCallback((points: Point2D[], debugLabel?: string): { points: Point2D[]; groupedPoints: Map<string, Point2D[]>; minX: number; maxX: number; minY: number; maxY: number } => {
        if (points.length === 0) return { points: [], groupedPoints: new Map(), minX: 0, maxX: 0, minY: 0, maxY: 0 };

        const xValues = points.map(p => p.x);
        const yValues = points.map(p => p.y);
        
        const minX = Math.min(...xValues);
        const maxX = Math.max(...xValues);
        const minY = Math.min(...yValues);
        const maxY = Math.max(...yValues);
        
        const rangeX = maxX - minX || 1;
        const rangeY = maxY - minY || 1;

        // Removed constant logging to reduce re-render noise
        // Uncomment for debugging if needed:
        // console.log(`=== NORMALIZATION DEBUG (${debugLabel || 'unknown'}) ===`);
        // console.log('Raw coordinates:', points.map(p => ({ x: p.x, y: p.y, id: p.entity?.id || p.query?.id })));
        // console.log('Ranges:', { minX, maxX, minY, maxY, rangeX, rangeY });
        
        const margin = 50;
        const canvasWidth = canvasSize.width - 2 * margin;
        const canvasHeight = canvasSize.height - 2 * margin;

        // First normalize all points
        const normalizedPoints = points.map(point => ({
            ...point,
            x: margin + ((point.x - minX) / rangeX) * canvasWidth,
            y: margin + (canvasHeight -((point.y - minY) / rangeY) * canvasHeight)
        }));

        // Group overlapping points by position (regardless of type)
        const groupedPoints = new Map<string, Point2D[]>();
        const tolerance = 3; // pixels

        normalizedPoints.forEach(point => {
            const key = `${Math.round(point.x / tolerance)}_${Math.round(point.y / tolerance)}`;
            
            if (!groupedPoints.has(key)) {
                groupedPoints.set(key, []);
            }
            groupedPoints.get(key)!.push(point);
        });

        // Create final points with overlap counts, separating by type
        const finalPoints: Point2D[] = [];
        groupedPoints.forEach((group, key) => {
            if (group.length === 1) {
                finalPoints.push(group[0]);
            } else {
                // Group by type for separate dots
                const entities = group.filter(p => p.entity && !p.isSearchResult);
                const queries = group.filter(p => p.query);
                const searchResults = group.filter(p => p.isSearchResult);
                
                // Create separate points for each type with slight offset
                let offsetIndex = 0;
                
                if (entities.length > 0) {
                    finalPoints.push({
                        ...entities[0],
                        overlapCount: entities.length,
                        overlapIndex: 0,
                        x: entities[0].x + (offsetIndex * 2), // Small offset for visual separation
                        y: entities[0].y + (offsetIndex * 2)
                    });
                    offsetIndex++;
                }
                if (queries.length > 0) {
                    finalPoints.push({
                        ...queries[0],
                        overlapCount: queries.length,
                        overlapIndex: 0,
                        x: queries[0].x + (offsetIndex * 2), // Small offset for visual separation
                        y: queries[0].y + (offsetIndex * 2)
                    });
                    offsetIndex++;
                }
                if (searchResults.length > 0) {
                    finalPoints.push({
                        ...searchResults[0],
                        overlapCount: searchResults.length,
                        overlapIndex: 0,
                        x: searchResults[0].x + (offsetIndex * 2), // Small offset for visual separation
                        y: searchResults[0].y + (offsetIndex * 2)
                    });
                    offsetIndex++;
                }
            }
        });

        return { 
            points: finalPoints, 
            groupedPoints,
            minX,
            maxX,
            minY,
            maxY
        };
    }, [canvasSize]);

    // Draw the visualization
    const drawVisualization = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas size
        canvas.width = canvasSize.width;
        canvas.height = canvasSize.height;

        // Clear canvas
        ctx.fillStyle = '#1f2937'; // gray-800
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        if (!usePCAMode && (!selectedDimensions.x || !selectedDimensions.y)) {
            // Draw placeholder
            ctx.fillStyle = '#6b7280'; // gray-500
            ctx.font = '16px system-ui';
            ctx.textAlign = 'center';
            ctx.fillText(
                'Select X and Y dimensions to visualize',
                canvas.width / 2,
                canvas.height / 2
            );
            return;
        }
        
        if (usePCAMode && (pcaSelectedDimensions.length < 2 || !pcaResult)) {
            // Draw placeholder for PCA
            ctx.fillStyle = '#6b7280'; // gray-500
            ctx.font = '16px system-ui';
            ctx.textAlign = 'center';
            if (pcaComputing) {
                ctx.fillText(
                    'Computing PCA...',
                    canvas.width / 2,
                    canvas.height / 2
                );
            } else {
                ctx.fillText(
                    'Select at least 2 dimensions for PCA',
                    canvas.width / 2,
                    canvas.height / 2
                );
            }
            return;
        }

        const points = getPoints2D();
        const { points: normalizedPoints } = normalizePoints(points, 'ALL_POINTS');

        if (normalizedPoints.length === 0) {
            // Draw no data message
            ctx.fillStyle = '#6b7280'; // gray-500
            ctx.font = '16px system-ui';
            ctx.textAlign = 'center';
            ctx.fillText(
                'No data available for selected dimensions',
                canvas.width / 2,
                canvas.height / 2
            );
            return;
        }

        // Draw grid based on actual coordinate values
        if (normalizedPoints.length > 0) {
            const originalXValues = points.map(p => p.x);
            const originalYValues = points.map(p => p.y);
            const originalMinX = Math.min(...originalXValues);
            const originalMaxX = Math.max(...originalXValues);
            const originalMinY = Math.min(...originalYValues);
            const originalMaxY = Math.max(...originalYValues);
            
            const rangeX = originalMaxX - originalMinX || 1;
            const rangeY = originalMaxY - originalMinY || 1;
            
            const margin = 50;
            const canvasWidth = canvas.width - 2 * margin;
            const canvasHeight = canvas.height - 2 * margin;
            
            // Draw grid lines based on coordinate values
            ctx.strokeStyle = '#374151'; // gray-700
            ctx.lineWidth = 1;
            
            // Calculate grid intervals (roughly 5-10 lines)
            const xGridCount = Math.min(10, Math.max(5, Math.ceil(rangeX)));
            const yGridCount = Math.min(10, Math.max(5, Math.ceil(rangeY)));
            
            const xGridStep = rangeX / xGridCount;
            const yGridStep = rangeY / yGridCount;
            
            // Draw vertical grid lines
            for (let i = 0; i <= xGridCount; i++) {
                const value = originalMinX + (i * xGridStep);
                const x = margin + ((value - originalMinX) / rangeX) * canvasWidth;
                
                ctx.beginPath();
                ctx.moveTo(x, margin);
                ctx.lineTo(x, canvas.height - margin);
                ctx.stroke();
                
                // Draw grid value labels (only for major intervals)
                if (i % Math.ceil(xGridCount / 5) === 0) {
                    ctx.fillStyle = '#9ca3af'; // gray-400
                    ctx.font = '10px system-ui';
                    ctx.textAlign = 'center';
                    ctx.fillText(value.toFixed(1), x, canvas.height - margin + 15);
                }
            }
            
            // Draw horizontal grid lines
            for (let i = 0; i <= yGridCount; i++) {
                const value = originalMinY + (i * yGridStep);
                const y = margin + (canvasHeight - ((value - originalMinY) / rangeY) * canvasHeight);
                
                ctx.beginPath();
                ctx.moveTo(margin, y);
                ctx.lineTo(canvas.width - margin, y);
                ctx.stroke();
                
                // Draw grid value labels (only for major intervals)
                if (i % Math.ceil(yGridCount / 5) === 0) {
                    ctx.fillStyle = '#9ca3af'; // gray-400
                    ctx.font = '10px system-ui';
                    ctx.textAlign = 'right';
                    // Y values now match the coordinate system directly
                    ctx.fillText(value.toFixed(1), margin - 10, y + 3);
                }
            }
            
            // Draw origin lines (0,0) if within range
            const originX = margin + ((0 - originalMinX) / rangeX) * canvasWidth;
            const originY = margin + (canvasHeight - ((0 - originalMinY) / rangeY) * canvasHeight);
            
            if (originX >= margin && originX <= canvas.width - margin) {
                ctx.strokeStyle = '#6b7280'; // gray-500
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(originX, margin);
                ctx.lineTo(originX, canvas.height - margin);
                ctx.stroke();
            }
            
            if (originY >= margin && originY <= canvas.height - margin) {
                ctx.strokeStyle = '#6b7280'; // gray-500
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(margin, originY);
                ctx.lineTo(canvas.width - margin, originY);
                ctx.stroke();
            }
            
            // Draw origin point
            if (originX >= margin && originX <= canvas.width - margin && 
                originY >= margin && originY <= canvas.height - margin) {
                ctx.fillStyle = '#9ca3af'; // gray-400
                ctx.beginPath();
                ctx.arc(originX, originY, 3, 0, 2 * Math.PI);
                ctx.fill();
            }
        } else {
            // Fallback grid when no points
            ctx.strokeStyle = '#374151'; // gray-700
            ctx.lineWidth = 1;
            const gridSize = 50;
            for (let x = 0; x < canvas.width; x += gridSize) {
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, canvas.height);
                ctx.stroke();
            }
            for (let y = 0; y < canvas.height; y += gridSize) {
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(canvas.width, y);
                ctx.stroke();
            }
        }

        // Draw axis labels
        ctx.fillStyle = '#d1d5db'; // gray-300
        ctx.font = '12px system-ui';
        ctx.textAlign = 'center';
        if (usePCAMode) {
            ctx.fillText('PC1 (Principal Component 1)', canvas.width / 2, canvas.height - 10);
            
            ctx.save();
            ctx.translate(20, canvas.height / 2);
            ctx.rotate(-Math.PI / 2);
            ctx.fillText('PC2 (Principal Component 2)', 0, 0);
            ctx.restore();
        } else {
            if (selectedDimensions.x && selectedDimensions.y) {
                ctx.fillText(selectedDimensions.x.name, canvas.width / 2, canvas.height - 10);
                
                ctx.save();
                ctx.translate(20, canvas.height / 2);
                ctx.rotate(-Math.PI / 2);
                ctx.fillText(selectedDimensions.y.name, 0, 0);
                ctx.restore();
            }
        }

        // Draw search result coverage area FIRST (beneath all points)
        if (showCoverageHull) {
            const coveragePoints = getSearchResultCoverageArea();
            
            if (coveragePoints.length > 0) {
            // Use the SAME normalization as the main points to ensure alignment
            // We need to normalize coverage points using the same range as all visible points
            const allPoints = getPoints2D();
            const { minX, maxX, minY, maxY } = (() => {
                if (allPoints.length === 0) return { minX: 0, maxX: 1, minY: 0, maxY: 1 };
                
                const xValues = allPoints.map(p => p.x);
                const yValues = allPoints.map(p => p.y);
                return {
                    minX: Math.min(...xValues),
                    maxX: Math.max(...xValues),
                    minY: Math.min(...yValues),
                    maxY: Math.max(...yValues)
                };
            })();
            
            const rangeX = maxX - minX || 1;
            const rangeY = maxY - minY || 1;
            const hullMargin = 50;
            const hullCanvasWidth = canvasSize.width - 2 * hullMargin;
            const hullCanvasHeight = canvasSize.height - 2 * hullMargin;
            
            // Normalize coverage points using the SAME range as all visible points
            const normalizedCoveragePoints = coveragePoints.map(point => ({
                ...point,
                x: hullMargin + ((point.x - minX) / rangeX) * hullCanvasWidth,
                y: hullMargin + (hullCanvasHeight - ((point.y - minY) / rangeY) * hullCanvasHeight)
            }));
            
            // Calculate convex hull of the normalized points (this ensures perfect alignment)
            const hullPoints = getConvexHull(normalizedCoveragePoints);
            
            if (hullPoints.length > 2) {
                // Calculate pixel-based density heatmap within the hull
                const pixelDensity = calculateDensityHeatmap(hullPoints, normalizedCoveragePoints, canvasSize.width, canvasSize.height);
                
                // Find max density for normalization
                let maxDensity = 0;
                for (let y = 0; y < pixelDensity.length; y++) {
                    for (let x = 0; x < pixelDensity[y].length; x++) {
                        maxDensity = Math.max(maxDensity, pixelDensity[y][x]);
                    }
                }
                
                // Draw pixel-based density heatmap
                for (let y = 0; y < pixelDensity.length; y++) {
                    for (let x = 0; x < pixelDensity[y].length; x++) {
                        const density = pixelDensity[y][x];
                        if (density > 0) {
                            // Normalize density to 0-1 range
                            const normalizedDensity = maxDensity > 0 ? density / maxDensity : 0;
                            
                            // Create color gradient from light blue (low density) to bright blue (high density)
                            // Higher intensity for better visibility of ranking differences
                            const intensity = Math.min(0.9, 0.2 + normalizedDensity * 0.7); // 0.2 to 0.9 opacity
                            const hue = 220; // Blue hue
                            const saturation = 80 + normalizedDensity * 20; // 80% to 100% saturation
                            const lightness = 60 - normalizedDensity * 30; // 60% to 30% lightness
                            
                            ctx.fillStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${intensity})`;
                            ctx.fillRect(x, y, 1, 1); // Draw single pixel
                        }
                    }
                }
                
                // Draw the hull border
                ctx.beginPath();
                ctx.moveTo(hullPoints[0].x, hullPoints[0].y);
                
                for (let i = 1; i < hullPoints.length; i++) {
                    ctx.lineTo(hullPoints[i].x, hullPoints[i].y);
                }
                ctx.closePath();
                
                // Draw the border
                ctx.strokeStyle = 'rgba(59, 130, 246, 0.6)'; // blue-500 with higher opacity for visibility
                ctx.lineWidth = 2;
                ctx.stroke();
            } else if (hullPoints.length === 2) {
                // Draw a line between two points
                ctx.beginPath();
                ctx.moveTo(hullPoints[0].x, hullPoints[0].y);
                ctx.lineTo(hullPoints[1].x, hullPoints[1].y);
                ctx.strokeStyle = 'rgba(59, 130, 246, 0.4)';
                ctx.lineWidth = 2;
                ctx.stroke();
            }
            }
        }

        // Draw points
        normalizedPoints.forEach(point => {
            const overlapCount = point.overlapCount || 1;
            const baseRadius = point.isSearchResult ? 8 : 4;
            
            // Calculate radius based on area (area = π * r², so r = √(area/π))
            // Base area = π * baseRadius²
            // For overlaps: area = baseArea * overlapCount
            const baseArea = Math.PI * baseRadius * baseRadius;
            const totalArea = baseArea * overlapCount;
            const radius = Math.sqrt(totalArea / Math.PI);
            
            if (point.isSearchResult) {
                // Search results as a cross in bright blue with glow effect
                ctx.shadowColor = 'rgba(59, 130, 246, 0.5)';
                ctx.shadowBlur = 15;
                
                const crossSize = radius * 1.5; // Make cross slightly larger than the circle would be
                const lineWidth = Math.max(3, radius * 0.3); // Proportional line width
                
                ctx.beginPath();
                // Draw horizontal line of cross
                ctx.moveTo(point.x - crossSize, point.y);
                ctx.lineTo(point.x + crossSize, point.y);
                // Draw vertical line of cross
                ctx.moveTo(point.x, point.y - crossSize);
                ctx.lineTo(point.x, point.y + crossSize);
                
                ctx.strokeStyle = 'rgba(29, 78, 216, 0.9)'; // blue-700 with transparency
                ctx.lineWidth = lineWidth;
                ctx.lineCap = 'round'; // Rounded line ends
                ctx.stroke();
                ctx.shadowBlur = 0; // Reset shadow
            } else {
                ctx.beginPath();
                ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI);
                
                if (point.query) {
                    // Queries in orange with transparency
                    ctx.fillStyle = 'rgba(249, 115, 22, 0.7)'; // orange-500 with transparency
                    ctx.strokeStyle = 'rgba(234, 88, 12, 0.8)'; // orange-600 with transparency
                    ctx.lineWidth = 1;
                } else {
                    // Check if we have active query matches and color entities accordingly
                    const hasActiveMatches = Object.keys(queryMatches).length > 0;
                    if (hasActiveMatches && point.entity) {
                        const entityId = point.entity.id;
                        const matchType = queryMatches[entityId];
                        const matchColors = getMatchTypeColor(matchType);
                        ctx.fillStyle = matchColors.fill;
                        ctx.strokeStyle = matchColors.stroke;
                        ctx.lineWidth = matchType ? 2 : 1; // Thicker border for matched entities
                    } else {
                        // Default: Entities in green with transparency
                        ctx.fillStyle = 'rgba(16, 185, 129, 0.7)'; // emerald-500 with transparency
                        ctx.strokeStyle = 'rgba(5, 150, 105, 0.8)'; // emerald-600 with transparency
                        ctx.lineWidth = 1;
                    }
                }
                
                ctx.fill();
                ctx.stroke();
            }
        });

        // Draw vector field arrows (query → matched entities direction)
        if (showVectorField) {
            const ARROW_LENGTH = 30;
            const ARROW_HEAD_LENGTH = 6;
            const ARROW_HEAD_ANGLE = Math.PI / 6;

            const entityPointMap = new Map<string, Point2D>();
            normalizedPoints.forEach(p => {
                if (p.entity) entityPointMap.set(p.entity.id, p);
            });

            const useFullLength = vectorFieldLength === 'full';

            const drawArrow = (fromX: number, fromY: number, toX: number, toY: number, style: string, width: number) => {
                const dx = toX - fromX;
                const dy = toY - fromY;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < 0.001) return;

                let endX: number, endY: number;
                if (useFullLength) {
                    endX = toX;
                    endY = toY;
                } else {
                    const dirX = (dx / dist) * ARROW_LENGTH;
                    const dirY = (dy / dist) * ARROW_LENGTH;
                    endX = fromX + dirX;
                    endY = fromY + dirY;
                }
                const angle = Math.atan2(dy, dx);

                ctx.strokeStyle = style;
                ctx.lineWidth = width;
                ctx.setLineDash([]);
                ctx.lineCap = 'round';
                ctx.beginPath();
                ctx.moveTo(fromX, fromY);
                ctx.lineTo(endX, endY);
                ctx.stroke();

                ctx.beginPath();
                ctx.moveTo(endX, endY);
                ctx.lineTo(
                    endX - ARROW_HEAD_LENGTH * Math.cos(angle - ARROW_HEAD_ANGLE),
                    endY - ARROW_HEAD_LENGTH * Math.sin(angle - ARROW_HEAD_ANGLE)
                );
                ctx.moveTo(endX, endY);
                ctx.lineTo(
                    endX - ARROW_HEAD_LENGTH * Math.cos(angle + ARROW_HEAD_ANGLE),
                    endY - ARROW_HEAD_LENGTH * Math.sin(angle + ARROW_HEAD_ANGLE)
                );
                ctx.stroke();
            };

            const activeId = vectorFieldShimmer ? shimmerActiveQueryId : focusedVectorQueryId;
            const hasFocus = activeId != null;

            normalizedPoints.forEach(point => {
                if (!point.query || !point.query.query_matches) return;

                const positiveMatches = point.query.query_matches.filter(m =>
                    m.match_type === 'perfect_match' || m.match_type === 'good_match' || m.match_type === 'match'
                );

                if (positiveMatches.length === 0) return;

                const isFocused = hasFocus && point.query.id === activeId;
                const isDimmed = hasFocus && !isFocused;

                if (vectorFieldMode === 'individual') {
                    positiveMatches.forEach(match => {
                        const entityPoint = entityPointMap.get(match.entity_id);
                        if (!entityPoint) return;
                        const alpha = isDimmed ? 0.08 : isFocused ? 0.9 : 0.5;
                        const width = isFocused ? 2 : 1;
                        drawArrow(point.x, point.y, entityPoint.x, entityPoint.y, `rgba(147, 197, 253, ${alpha})`, width);
                    });
                } else {
                    const matchedEntityPoints: Point2D[] = [];
                    positiveMatches.forEach(match => {
                        const ep = entityPointMap.get(match.entity_id);
                        if (ep) matchedEntityPoints.push(ep);
                    });
                    if (matchedEntityPoints.length === 0) return;

                    const centroidX = matchedEntityPoints.reduce((sum, p) => sum + p.x, 0) / matchedEntityPoints.length;
                    const centroidY = matchedEntityPoints.reduce((sum, p) => sum + p.y, 0) / matchedEntityPoints.length;
                    const alpha = isDimmed ? 0.08 : isFocused ? 0.9 : 0.7;
                    const width = isFocused ? 2.5 : 1.5;
                    drawArrow(point.x, point.y, centroidX, centroidY, `rgba(147, 197, 253, ${alpha})`, width);
                }
            });
        }

        // Draw dimension vectors in PCA mode
        if (usePCAMode && pcaResult && normalizedPoints.length > 0) {
            const originalXValues = points.map(p => p.x);
            const originalYValues = points.map(p => p.y);
            const originalMinX = Math.min(...originalXValues);
            const originalMaxX = Math.max(...originalXValues);
            const originalMinY = Math.min(...originalYValues);
            const originalMaxY = Math.max(...originalYValues);
            
            const rangeX = originalMaxX - originalMinX || 1;
            const rangeY = originalMaxY - originalMinY || 1;
            const margin = 50;
            const canvasWidth = canvas.width - 2 * margin;
            const canvasHeight = canvas.height - 2 * margin;
            
            // Find origin in normalized space
            const originX = margin + ((0 - originalMinX) / rangeX) * canvasWidth;
            const originY = margin + (canvasHeight - ((0 - originalMinY) / rangeY) * canvasHeight);
            
            // Generate colors for vectors
            const colors = [
                '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', 
                '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
            ];
            
            // Draw dimension vectors
            pcaResult.dimensionVectors.forEach((vector, idx) => {
                const color = colors[idx % colors.length];
                
                // Scale vector for visualization (make it visible but not too long)
                const scale = Math.min(canvasWidth, canvasHeight) * 0.15; // 15% of canvas size
                const endX = originX + vector.x * scale;
                const endY = originY - vector.y * scale; // Flip Y for canvas coordinates
                
                // Draw arrow line
                ctx.strokeStyle = color;
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(originX, originY);
                ctx.lineTo(endX, endY);
                ctx.stroke();
                
                // Draw arrowhead
                const angle = Math.atan2(originY - endY, endX - originX);
                const arrowLength = 8;
                const arrowAngle = Math.PI / 6;
                
                ctx.beginPath();
                ctx.moveTo(endX, endY);
                ctx.lineTo(
                    endX - arrowLength * Math.cos(angle - arrowAngle),
                    endY + arrowLength * Math.sin(angle - arrowAngle)
                );
                ctx.moveTo(endX, endY);
                ctx.lineTo(
                    endX - arrowLength * Math.cos(angle + arrowAngle),
                    endY + arrowLength * Math.sin(angle + arrowAngle)
                );
                ctx.stroke();
                
                // Draw dimension label
                ctx.fillStyle = color;
                ctx.font = '11px system-ui';
                ctx.textAlign = 'left';
                ctx.textBaseline = 'middle';
                const labelX = endX + 5;
                const labelY = endY;
                ctx.fillText(vector.dimension.name, labelX, labelY);
            });
        }

        // Draw comparison arrow and highlight selected points
        if (isComparisonMode && selectedPoints[0] && selectedPoints[1]) {
            const points = getPoints2D();
            const { points: normalizedPoints } = normalizePoints(points);
            
            // Find normalized positions of selected points
            const point1Normalized = normalizedPoints.find(p => 
                (selectedPoints[0]?.entity && p.entity?.id === selectedPoints[0].entity.id) ||
                (selectedPoints[0]?.query && p.query?.id === selectedPoints[0].query.id)
            );
            const point2Normalized = normalizedPoints.find(p => 
                (selectedPoints[1]?.entity && p.entity?.id === selectedPoints[1].entity.id) ||
                (selectedPoints[1]?.query && p.query?.id === selectedPoints[1].query.id)
            );
            
            if (point1Normalized && point2Normalized) {
                // Draw arrow from point1 to point2
                const dx = point2Normalized.x - point1Normalized.x;
                const dy = point2Normalized.y - point1Normalized.y;
                const angle = Math.atan2(dy, dx);
                const arrowLength = Math.sqrt(dx * dx + dy * dy);
                const arrowHeadLength = 15;
                const arrowHeadAngle = Math.PI / 6;
                
                // Stop point before arrowhead
                const stopX = point2Normalized.x - Math.cos(angle) * 8;
                const stopY = point2Normalized.y - Math.sin(angle) * 8;
                
                // Generate colors for dimensions
                const colors = [
                    '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', 
                    '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
                    '#14b8a6', '#a855f7', '#f43f5e', '#0ea5e9', '#22c55e'
                ];
                
                // Draw main arrow with dimension contribution segments
                if (comparisonResult && comparisonResult.dimensionDifferences && comparisonResult.dimensionDifferences.length > 0) {
                    // Calculate total contribution magnitude for scaling
                    const totalAbsContribution = comparisonResult.dimensionDifferences.reduce(
                        (sum: number, d: any) => sum + Math.abs(d.difference), 0
                    );
                    
                    // Draw arrow as segments, each proportional to dimension contribution
                    let accumulatedLength = 0;
                    const startX = point1Normalized.x;
                    const startY = point1Normalized.y;
                    
                    comparisonResult.dimensionDifferences.forEach((diff: any, idx: number) => {
                        const contributionRatio = Math.abs(diff.difference) / totalAbsContribution;
                        const segmentLength = arrowLength * contributionRatio;
                        
                        const segStartX = startX + accumulatedLength * Math.cos(angle);
                        const segStartY = startY + accumulatedLength * Math.sin(angle);
                        const segEndX = startX + (accumulatedLength + segmentLength) * Math.cos(angle);
                        const segEndY = startY + (accumulatedLength + segmentLength) * Math.sin(angle);
                        
                        const color = colors[idx % colors.length];
                        const isHovered = hoveredDimensionId === diff.dimension.id;
                        
                        // Draw segment
                        ctx.strokeStyle = color;
                        ctx.globalAlpha = isHovered ? 1 : 0.7;
                        ctx.lineWidth = isHovered ? 6 : 4;
                        ctx.lineCap = 'round';
                        ctx.beginPath();
                        ctx.moveTo(segStartX, segStartY);
                        ctx.lineTo(segEndX, segEndY);
                        ctx.stroke();
                        
                        accumulatedLength += segmentLength;
                    });
                    
                    ctx.globalAlpha = 1;
                } else {
                    // Fallback: simple arrow if no comparison result
                    ctx.strokeStyle = '#fbbf24';
                    ctx.lineWidth = 4;
                    ctx.beginPath();
                    ctx.moveTo(point1Normalized.x, point1Normalized.y);
                    ctx.lineTo(stopX, stopY);
                    ctx.stroke();
                }
                
                // Draw contribution vectors as a chained path
                if (comparisonResult && comparisonResult.dimensionDifferences && comparisonResult.dimensionDifferences.length > 0) {
                    // Get the original coordinate differences to calculate proper scaling
                    const points = getPoints2D();
                    const normalized = normalizePoints(points);
                    const { minX, maxX, minY, maxY } = normalized;
                    const rangeX = maxX - minX || 1;
                    const rangeY = maxY - minY || 1;
                    const margin = 50;
                    const canvasWidth = canvas.width - 2 * margin;
                    const canvasHeight = canvas.height - 2 * margin;
                    
                    // Scale factor: how much does one unit in original space map to in normalized space?
                    const scaleX = canvasWidth / rangeX;
                    const scaleY = canvasHeight / rangeY;
                    
                    // Start path from point1
                    let currentX = point1Normalized.x;
                    let currentY = point1Normalized.y;
                    
                    if (usePCAMode && pcaResult) {
                        // PCA mode: use dimension vectors directly, scaled by actual difference
                        // Create a map from dimension ID to color and vector (matching dimension vectors)
                        const dimensionColorMap = new Map<string, string>();
                        const dimensionVectorMap = new Map<string, any>();
                        pcaResult.dimensionVectors.forEach((vector, idx) => {
                            dimensionColorMap.set(vector.dimension.id, colors[idx % colors.length]);
                            dimensionVectorMap.set(vector.dimension.id, vector);
                        });
                        
                        // Debug: Track total contribution
                        let totalVecX = 0;
                        let totalVecY = 0;
                        let totalScaledVecX = 0;
                        let totalScaledVecY = 0;
                        
                        // Draw each dimension's contribution vector as part of a chained path
                        // IMPORTANT: All vectors advance the position, even if they're too small to draw
                        // This ensures the path ends at the second point
                        // Use unsorted version to match the order of intermediate points
                        const dimensionsToPlot = comparisonResult.dimensionDifferencesUnsorted || comparisonResult.dimensionDifferences;
                        dimensionsToPlot.forEach((diff: any) => {
                            // Find the corresponding dimension vector from pcaResult
                            const dimensionVector = dimensionVectorMap.get(diff.dimension.id);
                            
                            if (dimensionVector) {
                                // Use the pre-calculated contributions from comparison result
                                // These are already correctly calculated by transforming dimension vectors through PCA
                                const vecX = diff.xContribution || 0;
                                const vecY = diff.yContribution || 0;
                                const vecMagnitude = Math.sqrt(vecX * vecX + vecY * vecY);
                                
                                totalVecX += vecX;
                                totalVecY += vecY;
                                    
                                    // Scale the contribution vector to normalized coordinate space
                                    // Use the same scaleX and scaleY that were calculated above
                                    // This ensures the vectors are in the same coordinate system as the points
                                    const scaledVecX = vecX * scaleX;
                                    const scaledVecY = -vecY * scaleY; // Flip Y for canvas coordinates
                                    
                                    totalScaledVecX += scaledVecX;
                                    totalScaledVecY += scaledVecY;
                                    
                                // Calculate end position for ALL vectors (not just visible ones)
                                    const endX = currentX + scaledVecX;
                                    const endY = currentY + scaledVecY;
                                    
                                // Only draw vectors above threshold, but always advance position
                                if (vecMagnitude > 0.0001) {
                                    // Get color from dimension vector map
                                    const color = dimensionColorMap.get(diff.dimension.id) || '#888888';
                                    const isHovered = hoveredDimensionId === diff.dimension.id;
                                    
                                    // Draw vector line
                                    ctx.strokeStyle = color;
                                    ctx.globalAlpha = isHovered ? 1 : 0.7;
                                    ctx.lineWidth = isHovered ? 4 : 3;
                                    ctx.setLineDash([5, 5]);
                                    ctx.beginPath();
                                    ctx.moveTo(currentX, currentY);
                                    ctx.lineTo(endX, endY);
                                    ctx.stroke();
                                    
                                    // Draw arrowhead
                                    const vecAngle = Math.atan2(scaledVecY, scaledVecX);
                                    const arrowLength = 8;
                                    const arrowAngle = Math.PI / 6;
                                    ctx.beginPath();
                                    ctx.moveTo(endX, endY);
                                    ctx.lineTo(
                                        endX - arrowLength * Math.cos(vecAngle - arrowAngle),
                                        endY - arrowLength * Math.sin(vecAngle - arrowAngle)
                                    );
                                    ctx.moveTo(endX, endY);
                                    ctx.lineTo(
                                        endX - arrowLength * Math.cos(vecAngle + arrowAngle),
                                        endY - arrowLength * Math.sin(vecAngle + arrowAngle)
                                    );
                                    ctx.stroke();
                                    
                                    // Draw dimension label at end of vector
                                    if (isHovered) {
                                        ctx.fillStyle = color;
                                        ctx.font = '11px system-ui';
                                        ctx.textAlign = 'left';
                                        ctx.fillText(diff.dimension.name, endX + 5, endY);
                                    }
                                    }
                                    
                                // Update current position for next vector (ALWAYS, not just for visible ones)
                                // This ensures the path ends at the second point
                                    currentX = endX;
                                    currentY = endY;
                            }
                        });
                        
                        // Debug: Compare totals with expected
                        const expectedDiffX = comparisonResult.coordinateDiff.x;
                        const expectedDiffY = comparisonResult.coordinateDiff.y;
                        const expectedScaledX = expectedDiffX * scaleX;
                        const expectedScaledY = -expectedDiffY * scaleY;
                        const actualDiffX = point2Normalized.x - point1Normalized.x;
                        const actualDiffY = point2Normalized.y - point1Normalized.y;
                        
                        // Debug: Compare totals with expected - only log once per unique comparison
                        const debugKey = `${selectedPoints[0]?.entity?.id || selectedPoints[0]?.query?.id},${selectedPoints[1]?.entity?.id || selectedPoints[1]?.query?.id}`;
                        const lastDebugKey = (window as any).__lastContributionDebugKey;
                        if (lastDebugKey !== debugKey) {
                            (window as any).__lastContributionDebugKey = debugKey;
                            console.log(`[CONTRIBUTION DEBUG] Summary:`, {
                                'Total vec (original space)': { x: totalVecX.toFixed(4), y: totalVecY.toFixed(4) },
                                'Expected diff (original space)': { x: expectedDiffX.toFixed(4), y: expectedDiffY.toFixed(4) },
                                'Match?': Math.abs(totalVecX - expectedDiffX) < 0.01 && Math.abs(totalVecY - expectedDiffY) < 0.01,
                                'Total scaled vec (canvas space)': { x: totalScaledVecX.toFixed(2), y: totalScaledVecY.toFixed(2) },
                                'Expected scaled diff (canvas space)': { x: expectedScaledX.toFixed(2), y: expectedScaledY.toFixed(2) },
                                'Actual diff (canvas space)': { x: actualDiffX.toFixed(2), y: actualDiffY.toFixed(2) },
                                'Final position': { x: currentX.toFixed(2), y: currentY.toFixed(2) },
                                'Point 2 position': { x: point2Normalized.x.toFixed(2), y: point2Normalized.y.toFixed(2) },
                                'Difference (error)': { x: (point2Normalized.x - currentX).toFixed(2), y: (point2Normalized.y - currentY).toFixed(2) },
                                'scaleX': scaleX.toFixed(4),
                                'scaleY': scaleY.toFixed(4),
                                'rangeX': rangeX.toFixed(4),
                                'rangeY': rangeY.toFixed(4),
                                'Point 1 original': { x: selectedPoints[0]?.x, y: selectedPoints[0]?.y },
                                'Point 2 original': { x: selectedPoints[1]?.x, y: selectedPoints[1]?.y }
                            });
                        }
                    } else {
                        // Normal mode: simple X and Y components
                        comparisonResult.dimensionDifferences.forEach((diff: any, idx: number) => {
                            if (diff.xContribution !== undefined && diff.yContribution !== undefined) {
                                const vecX = diff.xContribution;
                                const vecY = diff.yContribution;
                                const vecMagnitude = Math.sqrt(vecX * vecX + vecY * vecY);
                                
                                if (vecMagnitude > 0.0001) {
                                    const color = colors[idx % colors.length];
                                    const isHovered = hoveredDimensionId === diff.dimension.id;
                                    
                                    // In normal mode, contributions are already in the coordinate space
                                    // Scale to normalized canvas space
                                    const scaledVecX = vecX * scaleX;
                                    const scaledVecY = -vecY * scaleY; // Flip Y for canvas coordinates
                                    
                                    const endX = currentX + scaledVecX;
                                    const endY = currentY + scaledVecY;
                                    
                                    // Draw vector line
                                    ctx.strokeStyle = color;
                                    ctx.globalAlpha = isHovered ? 1 : 0.7;
                                    ctx.lineWidth = isHovered ? 4 : 3;
                                    ctx.setLineDash([5, 5]);
                                    ctx.beginPath();
                                    ctx.moveTo(currentX, currentY);
                                    ctx.lineTo(endX, endY);
                                    ctx.stroke();
                                    
                                    // Draw arrowhead
                                    const vecAngle = Math.atan2(scaledVecY, scaledVecX);
                                    const arrowLength = 8;
                                    const arrowAngle = Math.PI / 6;
                                    ctx.beginPath();
                                    ctx.moveTo(endX, endY);
                                    ctx.lineTo(
                                        endX - arrowLength * Math.cos(vecAngle - arrowAngle),
                                        endY - arrowLength * Math.sin(vecAngle - arrowAngle)
                                    );
                                    ctx.moveTo(endX, endY);
                                    ctx.lineTo(
                                        endX - arrowLength * Math.cos(vecAngle + arrowAngle),
                                        endY - arrowLength * Math.sin(vecAngle + arrowAngle)
                                    );
                                    ctx.stroke();
                                    
                                    // Draw dimension label at end of vector
                                    if (isHovered) {
                                        ctx.fillStyle = color;
                                        ctx.font = '11px system-ui';
                                        ctx.textAlign = 'left';
                                        ctx.fillText(diff.dimension.name, endX + 5, endY);
                                    }
                                    
                                    // Update current position for next vector
                                    currentX = endX;
                                    currentY = endY;
                                }
                            }
                        });
                    }
                    
                    ctx.setLineDash([]);
                    ctx.globalAlpha = 1;
                }
                
                // Draw arrowhead
                ctx.strokeStyle = '#fbbf24';
                ctx.lineWidth = 4;
                ctx.globalAlpha = 1;
                ctx.beginPath();
                ctx.moveTo(point2Normalized.x, point2Normalized.y);
                ctx.lineTo(
                    point2Normalized.x - arrowHeadLength * Math.cos(angle - arrowHeadAngle),
                    point2Normalized.y - arrowHeadLength * Math.sin(angle - arrowHeadAngle)
                );
                ctx.moveTo(point2Normalized.x, point2Normalized.y);
                ctx.lineTo(
                    point2Normalized.x - arrowHeadLength * Math.cos(angle + arrowHeadAngle),
                    point2Normalized.y - arrowHeadLength * Math.sin(angle + arrowHeadAngle)
                );
                ctx.stroke();
                
                // Draw distance and component labels
                const midX = (point1Normalized.x + point2Normalized.x) / 2;
                const midY = (point1Normalized.y + point2Normalized.y) / 2;
                ctx.fillStyle = '#fbbf24';
                ctx.font = '12px system-ui';
                ctx.textAlign = 'center';
                ctx.fillText(`d: ${arrowLength.toFixed(1)}`, midX, midY - 10);
                
                if (comparisonResult && comparisonResult.coordinateDiff) {
                    ctx.fillText(`ΔX: ${comparisonResult.coordinateDiff.x.toFixed(2)}`, midX, midY + 5);
                    ctx.fillText(`ΔY: ${comparisonResult.coordinateDiff.y.toFixed(2)}`, midX, midY + 20);
                }
            }
        }

        // Highlight selected points for comparison
        if (isComparisonMode) {
            const points = getPoints2D();
            const { points: normalizedPoints } = normalizePoints(points);
            
            selectedPoints.forEach((selectedPoint, index) => {
                if (!selectedPoint) return;
                
                const normalizedPoint = normalizedPoints.find(p => 
                    (selectedPoint.entity && p.entity?.id === selectedPoint.entity.id) ||
                    (selectedPoint.query && p.query?.id === selectedPoint.query.id)
                );
                
                if (normalizedPoint) {
                    // Draw highlight ring
                    ctx.strokeStyle = index === 0 ? '#f59e0b' : '#10b981'; // amber-500 for point 1, emerald-500 for point 2
                    ctx.lineWidth = 4;
                    ctx.beginPath();
                    ctx.arc(normalizedPoint.x, normalizedPoint.y, 20, 0, 2 * Math.PI);
                    ctx.stroke();
                    
                    // Draw number label
                    ctx.fillStyle = index === 0 ? '#f59e0b' : '#10b981';
                    ctx.font = 'bold 16px system-ui';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText((index + 1).toString(), normalizedPoint.x, normalizedPoint.y - 25);
                }
            });
        }

        // Draw hovered point with full size highlight
        if (hoveredPoint) {
            const overlapCount = hoveredPoint.overlapCount || 1;
            const baseRadius = hoveredPoint.isSearchResult ? 8 : 4;
            
            // Calculate the same area-based radius as the original point
            const baseArea = Math.PI * baseRadius * baseRadius;
            const totalArea = baseArea * overlapCount;
            const radius = Math.sqrt(totalArea / Math.PI);
            
            if (hoveredPoint.isSearchResult) {
                // Draw highlight around the cross
                const crossSize = radius * 1.5;
                const lineWidth = Math.max(3, radius * 0.3);
                
                ctx.beginPath();
                // Draw horizontal line highlight
                ctx.moveTo(hoveredPoint.x - crossSize - 3, hoveredPoint.y);
                ctx.lineTo(hoveredPoint.x + crossSize + 3, hoveredPoint.y);
                // Draw vertical line highlight
                ctx.moveTo(hoveredPoint.x, hoveredPoint.y - crossSize - 3);
                ctx.lineTo(hoveredPoint.x, hoveredPoint.y + crossSize + 3);
                
                ctx.strokeStyle = '#fbbf24'; // amber-400
                ctx.lineWidth = lineWidth + 4;
                ctx.lineCap = 'round';
                ctx.stroke();
                
                // Draw inner highlight
                ctx.beginPath();
                ctx.moveTo(hoveredPoint.x - crossSize - 1, hoveredPoint.y);
                ctx.lineTo(hoveredPoint.x + crossSize + 1, hoveredPoint.y);
                ctx.moveTo(hoveredPoint.x, hoveredPoint.y - crossSize - 1);
                ctx.lineTo(hoveredPoint.x, hoveredPoint.y + crossSize + 1);
                
                ctx.strokeStyle = '#f59e0b'; // amber-500
                ctx.lineWidth = lineWidth + 2;
                ctx.lineCap = 'round';
                ctx.stroke();
            } else {
                // Draw highlight ring around the full circle for non-search results
                ctx.beginPath();
                ctx.arc(hoveredPoint.x, hoveredPoint.y, radius + 3, 0, 2 * Math.PI);
                ctx.strokeStyle = '#fbbf24'; // amber-400
                ctx.lineWidth = 3;
                ctx.stroke();
                
                // Draw inner highlight ring
                ctx.beginPath();
                ctx.arc(hoveredPoint.x, hoveredPoint.y, radius + 1, 0, 2 * Math.PI);
                ctx.strokeStyle = '#f59e0b'; // amber-500
                ctx.lineWidth = 1;
                ctx.stroke();
            }
        }
    }, [canvasSize, selectedDimensions, getPoints2D, normalizePoints, hoveredPoint, getSearchResultCoverageArea, getConvexHull, showCoverageHull, calculateDensityHeatmap, entities, queries, searchResults, usePCAMode, pcaResult, pcaComputing, isComparisonMode, selectedPoints, weightAdjustedMode, queryWeights, showVectorField, vectorFieldMode, vectorFieldLength, focusedVectorQueryId, vectorFieldShimmer, shimmerActiveQueryId]);

    // Handle mouse move for hover effects
    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        // Calculate scaling factors to account for CSS scaling
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        
        // Adjust mouse coordinates to match canvas coordinate system
        const adjustedMouseX = mouseX * scaleX;
        const adjustedMouseY = mouseY * scaleY;
        
        setMousePosition({ x: mouseX, y: mouseY });

        const points = getPoints2D();
        const { points: normalizedPoints } = normalizePoints(points);

        // Find closest point using adjusted coordinates
        let closestPoint: Point2D | null = null;
        let minDistance = Infinity;

        normalizedPoints.forEach(point => {
            const distance = Math.sqrt(
                Math.pow(point.x - adjustedMouseX, 2) + Math.pow(point.y - adjustedMouseY, 2)
            );
            if (distance < 15 && distance < minDistance) {
                minDistance = distance;
                closestPoint = point;
            }
        });

        setHoveredPoint(closestPoint);
    }, [getPoints2D, normalizePoints, canvasSize]);

    // Handle canvas click for overlap viewer
    const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        // Calculate scaling factors to account for CSS scaling
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        
        // Adjust mouse coordinates to match canvas coordinate system
        const adjustedMouseX = mouseX * scaleX;
        const adjustedMouseY = mouseY * scaleY;

        const points = getPoints2D();
        const { points: normalizedPoints, groupedPoints } = normalizePoints(points);

        // Find closest point using adjusted coordinates
        let closestPoint: Point2D | null = null;
        let minDistance = Infinity;

        normalizedPoints.forEach(point => {
            const distance = Math.sqrt(
                Math.pow(point.x - adjustedMouseX, 2) + Math.pow(point.y - adjustedMouseY, 2)
            );
            if (distance < 15 && distance < minDistance) {
                minDistance = distance;
                closestPoint = point;
            }
        });

        // Handle vector field focus: click a query to isolate its arrows, click again or empty space to clear
        if (showVectorField) {
            const clickedQueryId = (closestPoint as Point2D | null)?.query?.id;
            if (clickedQueryId) {
                setFocusedVectorQueryId(prev => prev === clickedQueryId ? null : clickedQueryId);
            } else {
                setFocusedVectorQueryId(null);
            }
        }

        // Handle comparison mode selection
        if (isComparisonMode && closestPoint) {
            const point = closestPoint as Point2D;
            
            // Get original coordinates before normalization
            const points = getPoints2D();
            const originalPoint = points.find(p => 
                (point.entity && p.entity?.id === point.entity.id) ||
                (point.query && p.query?.id === point.query.id)
            );
            
            // Create point with coordinates
            const pointWithCoords = {
                ...point,
                x: originalPoint?.x ?? point.x,
                y: originalPoint?.y ?? point.y
            };
            
            // Check if clicking the same point (deselect)
            if (selectedPoints[0] && 
                selectedPoints[0].entity?.id === point.entity?.id && 
                selectedPoints[0].query?.id === point.query?.id) {
                // Deselect point 1
                onSelectedPointsChange([null, selectedPoints[1]]);
            } else if (selectedPoints[1] && 
                       selectedPoints[1].entity?.id === point.entity?.id && 
                       selectedPoints[1].query?.id === point.query?.id) {
                // Deselect point 2
                onSelectedPointsChange([selectedPoints[0], null]);
            } else {
                // Select new point with coordinates
                if (!selectedPoints[0]) {
                    // Select as first point
                    onSelectedPointsChange([pointWithCoords, selectedPoints[1]]);
                } else if (!selectedPoints[1]) {
                    // Select as second point
                    onSelectedPointsChange([selectedPoints[0], pointWithCoords]);
                } else {
                    // Replace first point
                    onSelectedPointsChange([pointWithCoords, selectedPoints[1]]);
                }
            }
            return;
        }

        // Normal mode: show overlap group
        if (closestPoint) {
            const point = closestPoint as Point2D;
            const tolerance = 3;
            const key = `${Math.round(point.x / tolerance)}_${Math.round(point.y / tolerance)}`;
            const overlapGroup = groupedPoints.get(key);
            
            if (overlapGroup && overlapGroup.length > 0) {
                // Create a combined group that includes all types at this position
                const combinedGroup: Point2D[] = [];
                
                // Add all entities (not search results)
                const entities = overlapGroup.filter((p: Point2D) => p.entity && !p.isSearchResult);
                entities.forEach((entity: Point2D) => {
                    combinedGroup.push({
                        ...entity,
                        entity: entity.entity,
                        query: undefined,
                        isSearchResult: false
                    });
                });
                
                // Add all queries
                const queries = overlapGroup.filter((p: Point2D) => p.query);
                queries.forEach((query: Point2D) => {
                    combinedGroup.push({
                        ...query,
                        entity: undefined,
                        query: query.query,
                        isSearchResult: false
                    });
                });
                
                // Add all search results
                const searchResults = overlapGroup.filter((p: Point2D) => p.isSearchResult);
                searchResults.forEach((searchResult: Point2D) => {
                    combinedGroup.push({
                        ...searchResult,
                        entity: searchResult.entity,
                        query: undefined,
                        isSearchResult: true
                    });
                });
                
                onOverlapGroupSelect?.(combinedGroup, 0);
            }
        } else {
            onOverlapGroupSelect?.(null, 0);
        }
    }, [getPoints2D, normalizePoints, isComparisonMode, selectedPoints, onSelectedPointsChange]);

    useEffect(() => {
        if (!showVectorField) setFocusedVectorQueryId(null);
    }, [showVectorField]);

    // Shimmer animation: cycle through queries in spatial order
    useEffect(() => {
        if (shimmerTimerRef.current) {
            clearInterval(shimmerTimerRef.current);
            shimmerTimerRef.current = null;
        }

        if (!vectorFieldShimmer || !showVectorField) {
            setShimmerActiveQueryId(null);
            return;
        }

        const pts = getPoints2D();
        const { points: normPts } = normalizePoints(pts);
        const queryPoints: ShimmerPoint[] = normPts
            .filter(p => p.query?.query_matches?.some(m =>
                m.match_type === 'perfect_match' || m.match_type === 'good_match' || m.match_type === 'match'
            ))
            .map(p => ({ id: p.query!.id, x: p.x, y: p.y }));

        if (queryPoints.length === 0) {
            setShimmerActiveQueryId(null);
            return;
        }

        shimmerOrderRef.current = computeShimmerOrder(queryPoints);
        shimmerIndexRef.current = 0;
        setShimmerActiveQueryId(shimmerOrderRef.current[0]);

        shimmerTimerRef.current = setInterval(() => {
            shimmerIndexRef.current = (shimmerIndexRef.current + 1) % shimmerOrderRef.current.length;
            setShimmerActiveQueryId(shimmerOrderRef.current[shimmerIndexRef.current]);
        }, 500);

        return () => {
            if (shimmerTimerRef.current) {
                clearInterval(shimmerTimerRef.current);
                shimmerTimerRef.current = null;
            }
        };
    }, [vectorFieldShimmer, showVectorField, queries.length, entities.length, getPoints2D, normalizePoints]);

    // Redraw when dependencies change
    useEffect(() => {
        drawVisualization();
    }, [drawVisualization]);

    const points = getPoints2D();
    const { points: normalizedPoints } = normalizePoints(points);

    return (
        <div className="flex flex-col h-full">
            {/* Canvas */}
            <div className={`flex-1 relative bg-gray-800 rounded-lg overflow-hidden min-h-[400px] ${
                weightAdjustedMode ? 'border-2 border-purple-500' : 'border border-gray-700'
            }`}>
                <canvas
                    ref={canvasRef}
                    className="w-full h-full"
                    style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%',
                        cursor: isComparisonMode ? 'pointer' : 'crosshair'
                    }}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={() => setHoveredPoint(null)}
                    onClick={handleCanvasClick}
                />
                
                {/* Loading overlay */}
                {(isLoading || pcaComputing) && (
                    <div className="absolute inset-0 bg-gray-800/80 flex items-center justify-center">
                        <div className="text-center">
                            <IconLoader2 className="animate-spin mx-auto mb-2 text-blue-400" size={32} />
                            <p className="text-gray-300">
                                {pcaComputing ? 'Computing PCA...' : 'Loading visualization data...'}
                            </p>
                        </div>
                    </div>
                )}
                
                {/* Weight-adjusted mode indicator */}
                {weightAdjustedMode && selectedDimensions.x && selectedDimensions.y && !usePCAMode && (() => {
                    // Detect if weights are 0-1 or 0-100 scale
                    const maxWeight = Object.keys(queryWeights).length > 0 ? Math.max(...Object.values(queryWeights)) : 100;
                    const isNormalized = maxWeight <= 1;
                    const normalizeWeight = (w: number) => isNormalized ? w : w / 100;
                    
                    const weightX = queryWeights[selectedDimensions.x.name] ?? (isNormalized ? 1 : 100);
                    const weightY = queryWeights[selectedDimensions.y.name] ?? (isNormalized ? 1 : 100);
                    
                    return (
                        <div className="absolute top-3 right-3 bg-purple-900/90 backdrop-blur-sm border border-purple-500 rounded-lg px-3 py-2 shadow-lg">
                            <div className="text-xs font-semibold text-purple-300 mb-1">Weight-Adjusted View</div>
                            <div className="text-xs text-purple-200/80 space-y-0.5">
                                <div className="flex justify-between gap-4">
                                    <span>X: {selectedDimensions.x.name.substring(0, 15)}{selectedDimensions.x.name.length > 15 ? '...' : ''}</span>
                                    <span className="font-mono">×{Math.sqrt(normalizeWeight(weightX)).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between gap-4">
                                    <span>Y: {selectedDimensions.y.name.substring(0, 15)}{selectedDimensions.y.name.length > 15 ? '...' : ''}</span>
                                    <span className="font-mono">×{Math.sqrt(normalizeWeight(weightY)).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    );
                })()}
                
                {/* Expandable Weights Panel for PCA mode */}
                {weightAdjustedMode && usePCAMode && Object.keys(queryWeights).length > 0 && (() => {
                    // Detect if weights are 0-1 or 0-100 scale
                    const maxWeight = Math.max(...Object.values(queryWeights));
                    const isNormalized = maxWeight <= 1;
                    const displayMultiplier = isNormalized ? 100 : 1;
                    
                    return (
                        <div className={`absolute top-3 right-3 bg-purple-900/95 backdrop-blur-sm border border-purple-500 rounded-lg shadow-lg transition-all duration-200 ${weightsExpanded ? 'max-h-96' : 'max-h-12'} overflow-hidden`}>
                            <button
                                onClick={() => setWeightsExpanded(!weightsExpanded)}
                                className="w-full px-3 py-2 flex items-center justify-between gap-2 text-purple-300 hover:text-purple-200"
                            >
                                <span className="text-xs font-semibold">Query Weights ({Object.keys(queryWeights).length})</span>
                                <span className={`transition-transform duration-200 ${weightsExpanded ? 'rotate-180' : ''}`}>▼</span>
                            </button>
                            {weightsExpanded && (
                                <div className="px-3 pb-3 max-h-80 overflow-y-auto">
                                    <div className="text-xs text-purple-200/60 mb-2">Ordered by weight (highest first)</div>
                                    <div className="space-y-1">
                                        {Object.entries(queryWeights)
                                            .sort(([, a], [, b]) => b - a)
                                            .map(([dimName, weight]) => {
                                                const displayWeight = weight * displayMultiplier;
                                                return (
                                                    <div key={dimName} className="flex items-center gap-2">
                                                        <div 
                                                            className="h-2 rounded-full bg-gradient-to-r from-purple-600 to-purple-400"
                                                            style={{ width: `${displayWeight}%`, minWidth: '2px' }}
                                                        />
                                                        <span className={`text-xs font-mono min-w-[32px] ${displayWeight >= 50 ? 'text-green-400' : displayWeight >= 20 ? 'text-yellow-400' : 'text-red-400'}`}>
                                                            {isNormalized ? (weight * 100).toFixed(0) : weight.toFixed(0)}
                                                        </span>
                                                        <span className="text-xs text-purple-200/80 truncate" title={dimName}>
                                                            {dimName.length > 25 ? dimName.substring(0, 25) + '...' : dimName}
                                                        </span>
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}
            </div>

            {/* Legend and Info */}
            <div className="mt-4 flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                    <span className="text-gray-300">Entities ({entities.length})</span>
                </div>
                {queries.length > 0 && (
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                        <span className="text-gray-300">Queries ({queries.length})</span>
                    </div>
                )}
                {searchResults.length > 0 && (
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-blue-500 rounded-full shadow-lg shadow-blue-500/50"></div>
                        <span className="text-gray-300">Search Query ({searchResults.length})</span>
                    </div>
                )}
            </div>

            {/* Hover Info */}
            {hoveredPoint && (() => {
                const tooltipW = 250;
                const tooltipH = 120;
                const gap = 20;
                const cursorX = mousePosition.x;
                const cursorY = mousePosition.y;
                const spaceRight = canvasSize.width - cursorX;
                const spaceBelow = canvasSize.height - cursorY;

                const left = spaceRight > tooltipW + gap
                    ? cursorX + gap
                    : cursorX - tooltipW - gap;
                const top = spaceBelow > tooltipH + gap
                    ? cursorY + gap
                    : cursorY - tooltipH - gap;

                return (
                <div 
                    className="absolute bg-gray-900 border border-gray-600 rounded-lg p-3 shadow-lg z-10 max-w-xs pointer-events-none"
                    style={{
                        left: Math.max(0, left),
                        top: Math.max(0, top)
                    }}
                >
                    {hoveredPoint.entity && (
                        <div>
                            <h4 className="font-medium text-white mb-1">
                                Entity {hoveredPoint.overlapCount && hoveredPoint.overlapCount > 1 ? `(${hoveredPoint.overlapCount} overlapping)` : ''}
                            </h4>
                            <p className="text-sm text-gray-300 mb-1">
                                {hoveredPoint.entity.content?.title || hoveredPoint.entity.content?.name || 'Untitled'}
                            </p>
                            
                            {/* Display image if available */}
                            {(() => {
                                const imageUrls = getImageUrls(hoveredPoint.entity);
                                if (imageUrls.length > 0) {
                                    return (
                                        <div className="mb-2">
                                            <img 
                                                src={imageUrls[0]} 
                                                alt={hoveredPoint.entity.content?.title || hoveredPoint.entity.content?.name || 'Entity image'} 
                                                className="w-full max-w-[200px] h-auto object-contain rounded"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                }}
                                            />
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                            
                            <div className="text-xs text-gray-400">
                                {(() => {
                                    const metadata = hoveredPoint.entity!.embedding?.metadata;
                                    const preferredKey = useNormalizedEmbeddings ? 'normalized_entity_dimension_index' : 'entity_dimension_index';
                                    const idx = metadata?.[preferredKey] ?? metadata?.['entity_dimension_index'];
                                    return (
                                        <>
                                            <div>X: {(idx?.[selectedDimensions.x?.name || ''] !== undefined
                                                ? idx[selectedDimensions.x?.name || '']
                                                : idx?.[selectedDimensions.x?.id || ''])?.toFixed(3)}</div>
                                            <div>Y: {(idx?.[selectedDimensions.y?.name || ''] !== undefined
                                                ? idx[selectedDimensions.y?.name || '']
                                                : idx?.[selectedDimensions.y?.id || ''])?.toFixed(3)}</div>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    )}
                    {hoveredPoint.query && (
                        <div>
                            <h4 className="font-medium text-white mb-1">
                                Query {hoveredPoint.overlapCount && hoveredPoint.overlapCount > 1 ? `(${hoveredPoint.overlapCount} overlapping)` : ''}
                            </h4>
                            <p className="text-sm text-gray-300 mb-1">
                                {hoveredPoint.query.text}
                            </p>
                            <div className="text-xs text-gray-400">
                                {(() => {
                                    const preferred = useNormalizedEmbeddings
                                        ? hoveredPoint.query!.normalized_oasis_embedding
                                        : hoveredPoint.query!.oasis_embedding;
                                    const emb = preferred ?? hoveredPoint.query!.oasis_embedding;
                                    return (
                                        <>
                                            <div>X: {(emb?.[selectedDimensions.x?.name || ''] !== undefined
                                                ? emb[selectedDimensions.x?.name || '']
                                                : emb?.[selectedDimensions.x?.id || ''])?.toFixed(3)}</div>
                                            <div>Y: {(emb?.[selectedDimensions.y?.name || ''] !== undefined
                                                ? emb[selectedDimensions.y?.name || '']
                                                : emb?.[selectedDimensions.y?.id || ''])?.toFixed(3)}</div>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    )}
                </div>
                );
            })()}


            {/* Floating Status Info Panel */}
            <StatusInfoPanel
                usePCAMode={usePCAMode}
                pcaResult={pcaResult}
                pcaSelectedDimensions={pcaSelectedDimensions}
                selectedDimensions={selectedDimensions}
                normalizedPoints={normalizedPoints}
                points={points}
            />
        </div>
    );
};

export default AnalysisVisualization;
